import { useEffect } from 'react';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import { useGetAccountGroupTraitsQuery } from '@frameio/graphql-client/generated';
import { isAccountAdobeManaged } from '@frameio/graphql-client/utils/isAccountAdobeManaged';
import { useIsSegmentLoaded } from '@frameio/segment/contexts';
import { useActiveAccountId } from '../../contexts';

type GroupTraits = {
  delinquent_at?: string | null;
  delinquent_grace_period_expires_at?: string | null;
  feature_flag_enabled_guest_seats: boolean;
  locked_at?: string | null;
  managed_by_adobe: boolean;
  unpaid_at?: string | null;
};

const LOCAL_STORAGE_KEY = 'group-traits-for-account';

function group(accountId: string, traits: GroupTraits) {
  if (!window.analytics) return;

  window.analytics.group(accountId, traits);
}

/**
 * @param skipGroupTraits - When true, skips GetAccountGroupTraits and Segment
 *   `group` (e.g. secure reviewer inbox where account traits are not queryable).
 */
export function useGroupCallOnAccountLoad(skipGroupTraits = false) {
  const { isSegmentLoaded } = useIsSegmentLoaded();
  const accountId = useActiveAccountId();
  const areGuestSeatsEnabled = useFeatureFlagEnabled('core.guest_seats');
  const { data } = useGetAccountGroupTraitsQuery({
    variables: {
      accountId,
    },
    skip: skipGroupTraits || !accountId,
  });

  const account = data?.account;

  useEffect(() => {
    if (!isSegmentLoaded || !account) return;

    const traits = {
      delinquent_at: account.delinquentAt,
      delinquent_grace_period_expires_at:
        account.delinquentGracePeriodExpiresAt,
      feature_flag_enabled_guest_seats: areGuestSeatsEnabled,
      locked_at: account.lockedAt,
      managed_by_adobe: isAccountAdobeManaged(account.managedBy),
      unpaid_at: account.unpaidAt,
    };

    // create string of all trait values to compare with previous values
    const traitValues = Object.values(traits).join(',');

    /* eslint-disable no-storage/no-browser-storage */
    const traitValuesForAccountId =
      window.localStorage.getItem(LOCAL_STORAGE_KEY);
    /* eslint-enable no-storage/no-browser-storage */

    const traitValueMap = traitValuesForAccountId
      ? JSON.parse(traitValuesForAccountId)
      : {};

    if (traitValues !== traitValueMap[account.id]) {
      group(account.id, traits);

      traitValueMap[account.id] = traitValues;

      // eslint-disable-next-line no-storage/no-browser-storage
      window.localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify(traitValueMap)
      );
    }
  }, [account, areGuestSeatsEnabled, isSegmentLoaded]);
}
